import { calculateAreaPrice, isAreaInRange, totals } from '@components/Checkout/config/utils/utils';

const calculateProductPrice = (dronePrices, totalArea) => {
  const baseProduct = dronePrices?.products?.[0];
  if (!baseProduct?.prices?.[0]) {
    return 0;
  }

  return baseProduct.prices[0].price * totalArea;
};

const calculateForestManagementPrice = (selectedForestManagementPlan, totalArea) =>
  selectedForestManagementPlan?.price ? selectedForestManagementPlan.price * totalArea : 0;

const calculateDiscountAmount = (couponDiscount) => couponDiscount?.coupon_discount_price || 0;

export const calculateDronePrices = ({
  properties,
  selectedPackage,
  selectedForestManagementPlan,
  couponDiscount,
  dronePrices
}) => {
  const totalArea = totals.area(properties);

  if (!dronePrices?.products?.length || !properties?.length) {
    return {
      totalArea: 0,
      propertiesTotalPrice: 0,
      packageTotalPrice: 0,
      forestManagementPlanTotalPrice: 0,
      discountAmount: 0,
      fullPrice: 0,
      discountedPrice: 0,
      propertiesHectarePrice: 0,
      packageHectarePrice: 0
    };
  }

  const baseProduct = dronePrices.products[0];
  const priceRange = baseProduct.prices?.find((range) => isAreaInRange(totalArea, range));

  const packageFromDronePrices = dronePrices.packages?.find(
    (pkg) => pkg.internal_name === selectedPackage?.internalName
  );

  const packageTotalPrice =
    selectedPackage && packageFromDronePrices
      ? calculateAreaPrice(totalArea, selectedPackage.price)
      : 0;

  const propertiesTotalPrice = priceRange ? calculateAreaPrice(totalArea, priceRange.price) : 0;

  // Calculate actual hectare prices (without minimum order amount applied yet)
  const propertiesHectarePrice = priceRange?.price ?? 0;
  const packageHectarePrice = selectedPackage?.price ?? 0;

  const forestManagementPlanTotalPrice = calculateForestManagementPrice(
    selectedForestManagementPlan,
    totalArea
  );
  const discountAmount = calculateDiscountAmount(couponDiscount);

  const productPrice = calculateProductPrice(dronePrices, totalArea);

  // Calculate total before applying minimum order amount
  const totalBeforeMinimum =
    propertiesTotalPrice + packageTotalPrice + forestManagementPlanTotalPrice;

  // Calculate minimum order amount as sum of all selected minimums
  let minimumOrderAmount = baseProduct.minimum_order_amount; // Base: 4170

  // Add package minimum (30 hectares worth)
  if (selectedPackage && packageFromDronePrices) {
    const packageMin = selectedPackage.price * 30; // 30 hectares minimum
    minimumOrderAmount += packageMin;
  }

  // Add forest management plan minimum (30 hectares worth)
  if (selectedForestManagementPlan) {
    const fmpMin = selectedForestManagementPlan.price * 30; // 30 hectares minimum
    minimumOrderAmount += fmpMin;
  }

  const totalWithMinimum = Math.max(totalBeforeMinimum, minimumOrderAmount);

  const minimumAdjustment = totalWithMinimum - totalBeforeMinimum;

  let adjustedPropertiesTotalPrice = propertiesTotalPrice;
  let adjustedPackageTotalPrice = packageTotalPrice;

  if (minimumAdjustment > 0 && totalBeforeMinimum > 0) {
    const propertiesRatio = propertiesTotalPrice / totalBeforeMinimum;
    const packageRatio = packageTotalPrice / totalBeforeMinimum;

    adjustedPropertiesTotalPrice = propertiesTotalPrice + minimumAdjustment * propertiesRatio;
    adjustedPackageTotalPrice = packageTotalPrice + minimumAdjustment * packageRatio;
  }

  const fullAmount = productPrice + adjustedPackageTotalPrice + forestManagementPlanTotalPrice;

  const discountedPrice = Math.max(totalWithMinimum - discountAmount, 0);

  const fullPrice = discountedPrice > fullAmount ? discountedPrice + discountAmount : fullAmount;

  return {
    totalArea,
    propertiesTotalPrice: adjustedPropertiesTotalPrice,
    packageTotalPrice: adjustedPackageTotalPrice,
    forestManagementPlanTotalPrice,
    discountAmount,
    fullPrice: fullPrice === discountedPrice ? null : fullPrice,
    discountedPrice,
    propertiesHectarePrice,
    packageHectarePrice
  };
};

export const calculateSubscriptionPrices = ({ properties, couponDiscount, hasDfr }) => {
  const hasErrors = properties.some((prop) => prop.plus_price_error);

  if (hasErrors || !properties?.length) {
    return {
      totalForestArea: 0,
      propertiesTotalPrice: 0,
      discountAmount: 0,
      discountedPrice: 0,
      propertiesHectarePrice: 0
    };
  }

  const totalForestArea = totals.forestArea(properties);

  let propertiesTotalPrice = 0;
  let MultiPlusDiscount = undefined;
  //Ugly fix to show 15% discount for multiple properties or already has DFR
  if (hasDfr) {
    // prop.plus_price is already 85% from backend
    const originalTotal = properties.reduce((acum, prop) => acum + prop.plus_price / 0.85, 0);
    propertiesTotalPrice = properties.reduce((acum, prop) => acum + prop.plus_price, 0);
    const multiPlusDiscountAmount = originalTotal - propertiesTotalPrice;
    MultiPlusDiscount = {
      discountAmount: multiPlusDiscountAmount,
      discountReason: 'Rabatt som Plusmedlem (15%)'
    };
  } else if (properties.length > 1 && !hasDfr) {
    // prop.plus_price is 100%
    propertiesTotalPrice = properties.reduce((acum, prop) => acum + prop.plus_price * 0.85, 0);
    const multiPlusDiscountAmount = properties.reduce(
      (acum, prop) => acum + prop.plus_price * 0.15,
      0
    );

    MultiPlusDiscount = {
      discountAmount: multiPlusDiscountAmount,
      discountReason: 'Rabatt vid köp av flera fastigheter (15%)'
    };
  } else {
    propertiesTotalPrice = properties.reduce((acum, prop) => acum + prop.plus_price, 0);
  }

  const propertiesHectarePrice = totalForestArea > 0 ? propertiesTotalPrice / totalForestArea : 0;

  const discountAmount = calculateDiscountAmount(couponDiscount);
  const discountedPrice = Math.max(propertiesTotalPrice - discountAmount, 0);

  return {
    totalForestArea,
    propertiesTotalPrice: propertiesTotalPrice,
    discountAmount,
    discountedPrice,
    propertiesHectarePrice,
    MultiPlusDiscount
  };
};
